import type { OfferDto, TreatStatusDto } from "@boomap/contracts";
import { useState } from "react";

import { useI18n } from "../i18n/index.ts";
import { TREAT_COLORS, TREAT_ORDER } from "../lib/spot-offer.ts";
import type { GeoPoint } from "../lib/types.ts";
import { SpotIcon } from "./spot-icon.tsx";

const NOTE_MAX_LENGTH = 140;

export interface DeclareSheetProps {
  readonly location: GeoPoint;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onSubmit: (input: { offer: OfferDto; note: string | null }) => void;
}

/**
 * Feuille de saisie d'une nouvelle pastille. Aucune adresse n'est demandée :
 * seul le point posé sur la carte est transmis.
 *
 * Un seul choix à faire — les bonbons. La décoration a été retirée de
 * l'interface : elle demandait une décision de plus pour une information que
 * personne ne venait chercher. `decorated` reste dans le contrat et en base,
 * simplement plus alimenté d'ici.
 */
export const DeclareSheet = (props: DeclareSheetProps): React.ReactElement => {
  const { t } = useI18n();
  const [treats, setTreats] = useState<TreatStatusDto>("available");
  const [note, setNote] = useState("");

  return (
    <section className="sheet" aria-labelledby="declare-title">
      <header className="sheet__header">
        <h2 id="declare-title">{t.declare.title}</h2>
        <button
          type="button"
          className="sheet__close"
          onClick={props.onCancel}
          aria-label={t.declare.cancelLabel}
        >
          ✕
        </button>
      </header>

      <fieldset className="kinds">
        <legend className="field__label">{t.declare.treatsLegend}</legend>
        {TREAT_ORDER.map((option) => {
          const selected = treats === option;
          const color = TREAT_COLORS[option];

          return (
            <label
              key={option}
              className={`kind ${selected ? "kind--selected" : ""}`}
              style={
                selected
                  ? {
                      borderColor: color,
                      backgroundColor: `color-mix(in oklab, ${color} 14%, transparent)`,
                    }
                  : undefined
              }
            >
              <input
                type="radio"
                name="treats"
                value={option}
                checked={selected}
                onChange={() => setTreats(option)}
              />
              <span className="kind__icon">
                <SpotIcon treats={option} size={32} />
              </span>
              <span className="kind__label">{t.treats[option].label}</span>
              <span className="kind__hint">{t.treats[option].hint}</span>
            </label>
          );
        })}
      </fieldset>

      <label className="field">
        <span className="field__label">{t.declare.noteLabel}</span>
        <textarea
          value={note}
          maxLength={NOTE_MAX_LENGTH}
          rows={2}
          placeholder={t.declare.notePlaceholder}
          onChange={(event) => setNote(event.target.value)}
        />
        <span className="field__counter">
          {note.length}/{NOTE_MAX_LENGTH}
        </span>
      </label>

      <p className="sheet__note">
        {t.declare.location({
          latitude: props.location.latitude.toFixed(5),
          longitude: props.location.longitude.toFixed(5),
        })}
      </p>

      {props.error !== null && (
        <p className="alert" role="alert">
          {props.error}
        </p>
      )}

      <div className="sheet__actions">
        <button type="button" className="button button--ghost" onClick={props.onCancel}>
          {t.declare.cancel}
        </button>
        <button
          type="button"
          className="button button--primary"
          disabled={props.busy}
          onClick={() =>
            props.onSubmit({
              offer: { treats, decorated: false },
              note: note.trim() === "" ? null : note,
            })
          }
        >
          {props.busy ? t.declare.submitting : t.declare.submit}
        </button>
      </div>
    </section>
  );
};
